import React, { type ComponentType } from 'react';
import noop from 'lodash/noop';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import SuccessProgressBar from '@atlaskit/progress-bar';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import type { AdditionalEventAttributes } from '@atlassian/jira-common-experience-tracking-analytics';
import OpTrigger from '@atlassian/jira-common-util-op-triggers/src/on-mount';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import type { EstimationStatistic } from '@atlassian/jira-software-report-charting-common/src/common/utils/format-estimate/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { isErrorTrackingEnabledWithSentry, isPollinatorTenant } from '../feature-flags';
import { velocityReportPageLoad } from '../services/utils/performance-analytics';
import { SprintsFetchStatus } from '../state/reducers/entities/sprints/types';
import ErrorEmptyState from './error-empty-state';
import VelocityErrorToSLA from './velocity-error-to-sla';

export type Props = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Chart: ComponentType<Record<any, any>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Grid: ComponentType<Record<any, any>>;
	sprintFetchStatus: (typeof SprintsFetchStatus)[keyof typeof SprintsFetchStatus];
	sprintFetchProgress: number;
	sprintFetchProgressUpdate?: string;
	sprintFetchError?: Error;
	estimationStatistic: EstimationStatistic;
	onExperienceSuccess: () => void;
	onExperienceFailure: (extraLocation?: string, attributes?: AdditionalEventAttributes) => void;
};

export default function VelocityReport(props: Props) {
	const {
		Chart,
		Grid,
		onExperienceSuccess,
		onExperienceFailure,
		sprintFetchStatus,
		sprintFetchProgress,
		sprintFetchProgressUpdate,
		sprintFetchError,
		estimationStatistic,
	} = props;

	const reportComponent = () => {
		switch (sprintFetchStatus) {
			case 'PENDING':
			case 'IN_PROGRESS':
				return (
					<Box xcss={progressBarContainerWrapperStyles}>
						<Stack space="space.200" xcss={progressBarContainerStyles}>
							<SuccessProgressBar
								value={sprintFetchProgress}
								ariaLabel={sprintFetchProgressUpdate}
							/>
							{sprintFetchProgressUpdate && (
								<Text align="center" as="p">
									{sprintFetchProgressUpdate}
								</Text>
							)}
						</Stack>
					</Box>
				);
			case 'SUCCESS':
				return (
					<ContextualAnalyticsData attributes={{ estimationStatistic }}>
						<Chart />
						<Grid />

						<OpTrigger op={onExperienceSuccess} />
					</ContextualAnalyticsData>
				);
			case 'FAILURE':
				return (
					<>
						<ErrorEmptyState />
						<VelocityErrorToSLA
							error={sprintFetchError ?? null}
							onExperienceFailure={onExperienceFailure}
						/>
					</>
				);
			default:
				return null;
		}
	};

	return (
		<Box xcss={containerStyles} data-testId="software-velocity-report.root-container">
			{__SPA__ && sprintFetchStatus === SprintsFetchStatus.SUCCESS && (
				<>
					<SubmitApdex
						appName="software-reports-velocity-report"
						ttrSlo={8000}
						ttiSlo={8000}
						extra={{
							isSentryEnabled: String(isErrorTrackingEnabledWithSentry()),
							synthetic: isPollinatorTenant(),
						}}
						// @ts-expect-error - TS2769 - No overload matches this call.
						metric={velocityReportPageLoad}
					/>
					<SpaStatePageReady />
				</>
			)}

			{reportComponent()}
			<ViewTracker />
		</Box>
	);
}

VelocityReport.defaultProps = {
	fetchError: null,
	onExperienceSuccess: noop,
	onExperienceFailure: noop,
};

const containerStyles = xcss({
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});

const progressBarContainerWrapperStyles = xcss({
	marginTop: 'space.800', // Put the progress bar approximately at the position where the `LazyPage` spinner is shown.
	paddingTop: 'space.1000',
	paddingBottom: 'space.1000',
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
});

const progressBarContainerStyles = xcss({
	margin: '0 auto',
	maxWidth: '550px',
});
